import { useCallback, useEffect, useRef, useState } from 'react';

import { MedicalImageViewOptions } from '@/shared/api/protocol-ts/model/dto_common_image_view_options_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import {
  albumModel,
  hasPhotoTransformFields,
  mapViewOptionsToTransform,
} from '@/entities/album';
import { assetsModel } from '@/entities/assets';

import {
  useImageTransforms,
  normalizeRotation,
} from '@/features/dentalPhotoToolbar';
import type { DentalPhotoEditTool } from '@/features/dentalPhotoToolbar';
import {
  DEFAULT_CROP_BOX,
  ZOOM_MIN,
  ZOOM_MAX,
} from '@/features/dentalPhotoToolbar/config';

type UsePhotoEditingParams = {
  currentPhotoID: string;
  photoContainerNodeRef: React.RefObject<HTMLDivElement | null>;
  imageNaturalAspectRef: React.MutableRefObject<number>;
  containerSize: { width: number; height: number };
  visible: boolean;
};

export const usePhotoEditing = (params: UsePhotoEditingParams) => {
  const {
    currentPhotoID,
    photoContainerNodeRef,
    imageNaturalAspectRef,
    containerSize,
    visible,
  } = params;

  const dispatch = useAppDispatch();

  const [activeTool, setActiveTool] = useState<DentalPhotoEditTool | null>(
    null,
  );

  const {
    transformState,
    totalRotation,
    rotate90CW,
    rotate90CCW,
    setFreeRotation,
    toggleMirror,
    zoomIn,
    zoomOut,
    setZoom,
    setPan,
    setCropBox,
    resetTransforms,
    initializeFromPersisted,
    isDirty,
    saveSnapshot,
    restoreSnapshot,
  } = useImageTransforms();

  const savedTransform = useAppSelector((state) => {
    const asset = assetsModel.selectors.selectAssetByID(currentPhotoID)(state);

    if (asset?.Kind.case !== 'Study') {
      return undefined;
    }

    const viewOptions = asset.Kind.value.MedicalImageFeatures?.ViewOptionsV2;

    if (!viewOptions || !hasPhotoTransformFields(viewOptions)) {
      return undefined;
    }

    return mapViewOptionsToTransform(
      viewOptions,
      imageNaturalAspectRef.current,
    );
  });

  const isEditing = activeTool !== null;
  const isZoomedIn = transformState.zoom > 1;

  const zoomRef = useRef(1);
  const isEditingRef = useRef(false);

  const panDragRef = useRef<{
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
  } | null>(null);

  // Sync refs for event handlers that shouldn't cause re-attachment
  zoomRef.current = transformState.zoom;
  isEditingRef.current = isEditing;

  // Load saved transforms when photo changes
  useEffect(() => {
    if (savedTransform) {
      imageNaturalAspectRef.current = savedTransform.imageAspectRatio;
      initializeFromPersisted(savedTransform);
    } else {
      resetTransforms();
    }
    // Only re-run when photo changes, not when callbacks update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhotoID]);

  // Wheel zoom — only active during editing
  useEffect(() => {
    const el = photoContainerNodeRef.current;

    if (!el || !visible) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (!isEditingRef.current) {
        return;
      }

      event.preventDefault();

      const factor = event.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(
        ZOOM_MIN,
        Math.min(ZOOM_MAX, zoomRef.current * factor),
      );

      setZoom(newZoom);
    };

    el.addEventListener('wheel', handleWheel, { passive: false });

    return () => el.removeEventListener('wheel', handleWheel);
  }, [visible, setZoom, photoContainerNodeRef]);

  const handlePanPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!isZoomedIn) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

      panDragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startPanX: transformState.panX,
        startPanY: transformState.panY,
      };
    },
    [isZoomedIn, transformState.panX, transformState.panY],
  );

  const handlePanPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!panDragRef.current) {
        return;
      }

      const dx =
        (event.clientX - panDragRef.current.startX) /
        (containerSize.width || 1);
      const dy =
        (event.clientY - panDragRef.current.startY) /
        (containerSize.height || 1);

      setPan(
        panDragRef.current.startPanX + dx,
        panDragRef.current.startPanY + dy,
      );
    },
    [setPan, containerSize.width, containerSize.height],
  );

  const handlePanPointerUp = useCallback(() => {
    panDragRef.current = null;
  }, []);

  const handleToolSelect = useCallback(
    (tool: DentalPhotoEditTool | null) => {
      // Deactivate current tool — cancel unsaved changes
      if (tool === null) {
        restoreSnapshot();
        setActiveTool(null);

        return;
      }

      if (tool === 'mirror') {
        const newFlipH = !transformState.flipHorizontal;

        toggleMirror();

        // Persist immediately when not in an editing mode.
        // During editing, the mirror change will be committed/reverted
        // together with other changes via Done/Cancel.
        if (!isEditing && currentPhotoID) {
          const normalizedRotation = normalizeRotation(totalRotation);
          const isDefault =
            normalizedRotation === 0 &&
            !newFlipH &&
            transformState.cropBox === null &&
            transformState.zoom === 1 &&
            transformState.panX === 0 &&
            transformState.panY === 0;

          if (isDefault) {
            dispatch(
              assetsModel.thunks.setMedicalImageViewOptions({
                AssetID: currentPhotoID,
                MedicalImageViewOptionsV2: new MedicalImageViewOptions({
                  Rotation: 0,
                  FlipHorizontal: false,
                  Zoom: 1,
                  PanX: 0,
                  PanY: 0,
                }),
              }),
            );
          } else {
            const transform = {
              rotation: normalizedRotation,
              flipHorizontal: newFlipH,
              cropBox: transformState.cropBox,
              imageAspectRatio: imageNaturalAspectRef.current,
              zoom: transformState.zoom,
              panX: transformState.panX,
              panY: transformState.panY,
            };

            dispatch(
              albumModel.thunks.setPhotoViewOptions({
                assetID: currentPhotoID,
                transform,
              }),
            )
              .unwrap()
              .then((response) => {
                if (response.Asset) {
                  dispatch(assetsModel.actions.setMany([response.Asset]));
                }
              });
          }
        }

        return;
      }

      saveSnapshot();

      if (tool === 'crop' && !transformState.cropBox) {
        setCropBox(DEFAULT_CROP_BOX);
      }

      setActiveTool(tool);
    },
    [
      restoreSnapshot,
      toggleMirror,
      transformState,
      totalRotation,
      setCropBox,
      saveSnapshot,
      isEditing,
      currentPhotoID,
      dispatch,
      imageNaturalAspectRef,
    ],
  );

  const handleDone = useCallback(() => {
    if (currentPhotoID) {
      const normalizedRotation = normalizeRotation(totalRotation);
      const hasTransform =
        normalizedRotation !== 0 ||
        transformState.flipHorizontal ||
        transformState.cropBox !== null ||
        transformState.zoom !== 1 ||
        transformState.panX !== 0 ||
        transformState.panY !== 0;

      if (hasTransform) {
        const transform = {
          rotation: normalizedRotation,
          flipHorizontal: transformState.flipHorizontal,
          cropBox: transformState.cropBox,
          imageAspectRatio: imageNaturalAspectRef.current,
          zoom: transformState.zoom,
          panX: transformState.panX,
          panY: transformState.panY,
        };

        dispatch(
          albumModel.thunks.setPhotoViewOptions({
            assetID: currentPhotoID,
            transform,
          }),
        )
          .unwrap()
          .then((response) => {
            if (response.Asset) {
              dispatch(assetsModel.actions.setMany([response.Asset]));
            }
          });
      } else {
        dispatch(
          assetsModel.thunks.setMedicalImageViewOptions({
            AssetID: currentPhotoID,
            MedicalImageViewOptionsV2: new MedicalImageViewOptions({
              Rotation: 0,
              FlipHorizontal: false,
              Zoom: 1,
              PanX: 0,
              PanY: 0,
            }),
          }),
        );
      }
    }

    setActiveTool(null);
  }, [
    currentPhotoID,
    transformState,
    totalRotation,
    dispatch,
    imageNaturalAspectRef,
  ]);

  const handleCancel = useCallback(() => {
    restoreSnapshot();
    setActiveTool(null);
  }, [restoreSnapshot]);

  const handleResetToOriginal = useCallback(() => {
    if (currentPhotoID) {
      dispatch(
        albumModel.thunks.setPhotoViewOptions({
          assetID: currentPhotoID,
          transform: {
            rotation: 0,
            flipHorizontal: false,
            cropBox: null,
            imageAspectRatio: imageNaturalAspectRef.current,
            zoom: 1,
            panX: 0,
            panY: 0,
          },
        }),
      )
        .unwrap()
        .then((response) => {
          if (response.Asset) {
            dispatch(assetsModel.actions.setMany([response.Asset]));
          }
        });
    }

    resetTransforms();
    setActiveTool(null);
  }, [resetTransforms, currentPhotoID, dispatch, imageNaturalAspectRef]);

  return {
    activeTool,
    setActiveTool,
    transformState,
    isEditing,
    isZoomedIn,
    isDirty,
    rotate90CW,
    rotate90CCW,
    setFreeRotation,
    totalRotation,
    zoomIn,
    zoomOut,
    setCropBox,
    handleToolSelect,
    handleDone,
    handleCancel,
    handleResetToOriginal,
    handlePanPointerDown,
    handlePanPointerMove,
    handlePanPointerUp,
  };
};
